import { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Grid,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useTranslation } from 'react-i18next';
import {
  executeCustomerTestCleanup,
  previewCustomerTestCleanup,
} from '../api/customersApi';
import { useAuth } from '../auth/AuthContext';
import { hasPermission } from '../auth/permissions';
import { colorTokens } from '../app/designTokens';
import {
  formatBillingInterval,
  formatCustomerStatus,
  formatModuleCode,
  formatRegistrationSource,
} from '../utils/customerPortalLabels';
import { DetailField } from './InfoCard';
import { LoadingSkeleton } from './LoadingSkeleton';
import { SectionCard } from './SectionCard';
import { Button } from './ui/Button';
import { TextInput } from './ui/TextInput';

const { semantic } = colorTokens;

const BLOCKER_LABELS = {
  cleanup_disabled: 'customers:cleanup.blockers.cleanup_disabled',
  production_blocked: 'customers:cleanup.blockers.production_blocked',
  paid_payment_exists: 'customers:cleanup.blockers.paid_payment_exists',
  paid_invoice_exists: 'customers:cleanup.blockers.paid_invoice_exists',
  active_paid_subscription: 'customers:cleanup.blockers.active_paid_subscription',
  tenant_launched: 'customers:cleanup.blockers.tenant_launched',
  tenant_production_flag: 'customers:cleanup.blockers.tenant_production_flag',
  confirmation_mismatch: 'customers:cleanup.blockers.confirmation_mismatch',
};

function formatCleanupWarning(warning, t) {
  if (warning === 'tenant_database_drop_disabled'
    || /Tenant database cleanup is disabled/i.test(String(warning))) {
    return t('customers:cleanup.tenant_db_drop_disabled');
  }
  if (typeof warning === 'string' && warning.startsWith('tenant_database_drop_unsafe:')) {
    return t('customers:cleanup.tenant_db_drop_unsafe', {
      name: warning.split(':').slice(1).join(':'),
      defaultValue: warning,
    });
  }
  return warning;
}

export function CustomerTestCleanupPanel({ customer }) {
  const { t } = useTranslation(['customers', 'common']);
  const { session } = useAuth();
  const canCleanup = hasPermission(session, 'system.customers.cleanup_test');

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmation, setConfirmation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadPreview = useCallback(async () => {
    if (!customer?.id || !canCleanup) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const data = await previewCustomerTestCleanup(customer.id);
      setPreview(data);
    } catch {
      setError(t('customers:cleanup.load_failed'));
    } finally {
      setLoading(false);
    }
  }, [canCleanup, customer?.id, t]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  const handleCleanup = async () => {
    setSubmitting(true);
    setMessage('');
    setError('');

    try {
      await executeCustomerTestCleanup(customer.id, confirmation);
      setMessage(t('customers:cleanup.success'));
      setConfirmation('');
    } catch (err) {
      const blockers = err.response?.data?.blockers || [];
      if (blockers.length > 0) {
        setError(blockers.map((key) => t(BLOCKER_LABELS[key] || key)).join(' · '));
      } else {
        setError(err.response?.data?.message || t('customers:cleanup.failed'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!canCleanup) {
    return null;
  }

  if (loading) {
    return <LoadingSkeleton type="Detail" />;
  }

  const paidGuard = preview?.blockers?.some((item) => [
    'paid_payment_exists',
    'paid_invoice_exists',
    'active_paid_subscription',
    'tenant_launched',
    'tenant_production_flag',
  ].includes(item));

  const confirmationTarget = customer.slug || customer.name;
  const confirmationOk = confirmation.trim().toLowerCase() === String(confirmationTarget).toLowerCase();

  return (
    <SectionCard variant="Danger" title={t('customers:cleanup.danger_zone_title')}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
        <WarningAmberIcon color="error" fontSize="small" sx={{ mt: 0.25 }} />
        <Typography sx={{ fontSize: 13, color: semantic.text.secondary }}>
          {t('customers:cleanup.danger_zone_hint')}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}

      {preview && (
        <>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Chip
              label={preview.eligible ? t('customers:cleanup.eligible') : t('customers:cleanup.not_eligible')}
              color={preview.eligible ? 'success' : 'default'}
              size="small"
            />
            {(preview.blockers || []).map((blocker) => (
              <Chip
                key={blocker}
                label={t(BLOCKER_LABELS[blocker] || blocker)}
                color="error"
                size="small"
              />
            ))}
          </Box>

          {(preview.warnings || []).length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {(preview.warnings || []).map((warning) => (
                <Typography key={warning} variant="body2">
                  {formatCleanupWarning(warning, t)}
                </Typography>
              ))}
            </Alert>
          )}

          <Typography sx={{ mb: 1, fontSize: 13, fontWeight: 600, color: semantic.text.primary }}>
            {t('customers:cleanup.will_delete')}
          </Typography>
          <Box component="ul" sx={{ mt: 0, mb: 2 }}>
            {(preview.will_delete || []).map((item) => (
              <Typography component="li" key={item} sx={{ fontSize: 13, color: semantic.text.secondary }}>
                {t(`customers:cleanup.items.${item}`, item)}
              </Typography>
            ))}
          </Box>

          {paidGuard && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t('customers:cleanup.paid_customer_guard')}
            </Alert>
          )}

          <Grid container spacing={2} alignItems="stretch">
            <Grid item xs={12} md={8}>
              <TextInput
                density="Compact"
                label={t('customers:cleanup.confirmation_label', { value: confirmationTarget })}
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                disabled={!preview.eligible || paidGuard}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="Danger"
                disabled={!preview.eligible || paidGuard || !confirmationOk || submitting}
                loading={submitting}
                onClick={handleCleanup}
                sx={{ height: '100%', minHeight: 40 }}
              >
                {t('customers:cleanup.submit')}
              </Button>
            </Grid>
          </Grid>
        </>
      )}
    </SectionCard>
  );
}

export function CustomerCompanyDataPanel({ customer, translateStatus }) {
  const { t } = useTranslation(['customers', 'common', 'statuses', 'customerPortal']);
  const { session } = useAuth();
  const isSystemAdmin = session?.user?.user_type === 'internal';

  const contact = customer.normalized_contact || {};
  const registration = customer.registration_summary || {};
  const tenant = customer.tenant_summary || {};
  const billing = customer.billing_summary || {};
  const emptyValue = t('common:empty_value');

  const modulesLabel = Array.isArray(registration.modules_selected)
    ? registration.modules_selected.map((code) => formatModuleCode(code)).filter(Boolean).join(', ')
    : (registration.modules_selected ? formatModuleCode(registration.modules_selected) : null);

  const fieldGridSx = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
    gap: 2.5,
  };

  return (
    <Box>
      <SectionCard variant="Default" title={t('customers:sections.company')}>
        <Box sx={fieldGridSx}>
          <DetailField label={t('common:name')} value={customer.name || emptyValue} />
          <DetailField
            label={t('customers:fields.country')}
            value={contact.country_name || contact.country_code || emptyValue}
          />
          <DetailField label={t('customers:fields.language')} value={contact.default_locale || emptyValue} />
          <DetailField label={t('customers:fields.timezone')} value={contact.timezone || emptyValue} />
          <DetailField label={t('customers:fields.currency')} value={contact.billing_currency || emptyValue} />
          <DetailField
            label={t('common:status')}
            value={formatCustomerStatus(customer.status) || translateStatus('customer', customer.status)}
          />
          <DetailField
            label={t('customers:fields.trial_status')}
            value={billing.is_trial ? t('customers:trial_active') : t('customers:trial_not_active')}
          />
        </Box>
      </SectionCard>

      <SectionCard variant="Default" title={t('customers:sections.contact')}>
        <Box sx={fieldGridSx}>
          <DetailField label={t('customers:fields.contact_person')} value={contact.contact_name || emptyValue} />
          <DetailField label={t('common:email')} value={contact.contact_email || emptyValue} />
          <DetailField
            label={t('customers:fields.phone')}
            value={contact.phone_display || contact.phone_e164 || emptyValue}
          />
        </Box>
      </SectionCard>

      {tenant?.id && (
        <SectionCard variant="Default" title={t('customers:sections.tenant')}>
          <Box sx={fieldGridSx}>
            <DetailField label={t('common:name')} value={tenant.name || emptyValue} />
            <DetailField label={t('common:slug')} value={tenant.slug || emptyValue} />
            <DetailField
              label={t('customers:fields.primary_domain')}
              value={tenant.primary_domain || emptyValue}
            />
            <DetailField
              label={t('common:status')}
              value={translateStatus('tenant', tenant.status)}
            />
          </Box>
        </SectionCard>
      )}

      <SectionCard variant="Default" title={t('customers:sections.registration')}>
        <Box sx={fieldGridSx}>
          <DetailField
            label={t('customers:fields.registration_source')}
            value={formatRegistrationSource(registration.source) || registration.source || emptyValue}
          />
          <DetailField
            label={t('customers:fields.modules_selected')}
            value={modulesLabel || emptyValue}
          />
          <DetailField
            label={t('customers:fields.billing_interval')}
            value={formatBillingInterval(registration.billing_interval) || registration.billing_interval || emptyValue}
          />
          <DetailField
            label={t('customers:fields.users_count')}
            value={registration.users_count ?? emptyValue}
          />
          {registration.utm && (
            <DetailField
              label={t('customers:fields.utm')}
              value={Object.entries(registration.utm).map(([key, value]) => `${key}: ${value}`).join(' · ')}
            />
          )}
        </Box>
      </SectionCard>

      {isSystemAdmin && customer.technical_metadata && Object.keys(customer.technical_metadata).length > 0 && (
        <SectionCard variant="Default">
          <Accordion
            disableGutters
            elevation={0}
            sx={{
              bgcolor: 'transparent',
              backgroundImage: 'none',
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: semantic.text.muted }} />}>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: semantic.text.primary }}>
                {t('customers:sections.technical_metadata')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 2,
                  bgcolor: semantic.bg.input,
                  border: `1px solid ${semantic.border.subtle}`,
                  borderRadius: 1,
                  overflow: 'auto',
                  fontSize: 12,
                  color: semantic.text.secondary,
                }}
              >
                {JSON.stringify(customer.technical_metadata, null, 2)}
              </Box>
            </AccordionDetails>
          </Accordion>
        </SectionCard>
      )}
    </Box>
  );
}
